import { useNavigate } from 'react-router-dom';
import styles from './Legal.module.css';
import Navigation from '../Home/Navigation';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.container}>
      <Navigation />
      
      <main className={styles.mainContent}>
        <div className={styles.header}>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ← Back
          </button>
          <h1 className={styles.title}>Terms of Service</h1>
          <p className={styles.lastUpdated}>Last Updated: November 24, 2025</p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
            <p className={styles.paragraph}>
              Welcome to Kingdom Studio. By accessing or using our AI-powered video creation platform ("Service"), 
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
              please do not use our Service.
            </p>
            <p className={styles.paragraph}>
              These Terms constitute a legally binding agreement between you and Kingdom Studio. 
              We reserve the right to modify these Terms at any time, and your continued use of the Service 
              constitutes acceptance of any changes.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>2. Eligibility</h2>
            <p className={styles.paragraph}>
              You must be at least 13 years old to use our Service. By using the Service, you represent and warrant that:
            </p>
            <ul className={styles.list}>
              <li>You are at least 13 years of age</li>
              <li>You have the legal capacity to enter into these Terms</li>
              <li>You will comply with all applicable laws and regulations</li>
              <li>All information you provide is accurate and current</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>3. Account Registration</h2>
            <p className={styles.paragraph}>
              To access certain features of our Service, you must create an account. You agree to:
            </p>
            <ul className={styles.list}>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className={styles.paragraph}>
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>4. Service Description</h2>
            <p className={styles.paragraph}>
              Kingdom Studio provides AI-powered tools for video creation, including:
            </p>
            <ul className={styles.list}>
              <li>AI Script Generator</li>
              <li>AI Prompt Generator</li>
              <li>AI Audio and Voice Generator</li>
              <li>AI Video Generator</li>
              <li>Video Editor and Caption Tools</li>
              <li>AI Soundtrack and Sound Effects Generator</li>
              <li>Thumbnail Generator</li>
              <li>Social Media Publishing Tools</li>
            </ul>
            <p className={styles.paragraph}>
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time without notice.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>5. Subscription Plans and Payment</h2>
            
            <h3 className={styles.subsectionTitle}>5.1 Pricing and Plans</h3>
            <p className={styles.paragraph}>
              We offer various subscription plans with different features and usage limits. 
              Pricing is subject to change with notice. Current pricing can be found on our pricing page.
            </p>

            <h3 className={styles.subsectionTitle}>5.2 Billing</h3>
            <ul className={styles.list}>
              <li>Subscriptions are billed in advance on a recurring basis (monthly or annually)</li>
              <li>Payment is due at the start of each billing period</li>
              <li>We use third-party payment processors to handle transactions securely</li>
              <li>You authorize us to charge your payment method for all fees incurred</li>
            </ul>

            <h3 className={styles.subsectionTitle}>5.3 Refunds and Cancellations</h3>
            <p className={styles.paragraph}>
              You may cancel your subscription at any time. Upon cancellation, you will retain access until the end of your 
              current billing period. We generally do not provide refunds for partial subscription periods, except as required by law.
            </p>

            <h3 className={styles.subsectionTitle}>5.4 Free Trials and Promotions</h3>
            <p className={styles.paragraph}>
              Free trials and promotional offers are subject to specific terms and conditions. 
              After a free trial, you will be automatically charged unless you cancel before the trial ends.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>6. User Content and Intellectual Property</h2>
            
            <h3 className={styles.subsectionTitle}>6.1 Your Content</h3>
            <p className={styles.paragraph}>
              You retain ownership of all content you create, upload, or generate using our Service ("User Content"). 
              By using the Service, you grant us a worldwide, non-exclusive, royalty-free license to:
            </p>
            <ul className={styles.list}>
              <li>Store, process, and display your User Content to provide the Service</li>
              <li>Use your inputs to train and improve our AI models (anonymized)</li>
              <li>Create backups and derivative works necessary for Service operation</li>
            </ul>

            <h3 className={styles.subsectionTitle}>6.2 AI-Generated Content</h3>
            <p className={styles.paragraph}>
              Content generated by our AI tools based on your inputs is owned by you, subject to these Terms. 
              However, you acknowledge that AI-generated content may not be unique and similar content may be generated for other users.
            </p>

            <h3 className={styles.subsectionTitle}>6.3 Our Intellectual Property</h3>
            <p className={styles.paragraph}>
              The Service, including all software, algorithms, designs, trademarks, and content provided by us, 
              is owned by Kingdom Studio and protected by intellectual property laws. You may not copy, modify, 
              distribute, or reverse engineer any part of the Service.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>7. Acceptable Use Policy</h2>
            <p className={styles.paragraph}>
              You agree not to use the Service to:
            </p>
            <ul className={styles.list}>
              <li>Create content that is illegal, harmful, threatening, abusive, harassing, defamatory, or discriminatory</li>
              <li>Violate any intellectual property rights or privacy rights of others</li>
              <li>Generate spam, malware, or phishing content</li>
              <li>Create deepfakes or misleading content without proper disclosure</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use the Service for any commercial purpose without authorization</li>
              <li>Scrape, data mine, or extract data from the Service using automated means</li>
              <li>Resell or redistribute the Service or AI-generated content</li>
            </ul>
            <p className={styles.paragraph}>
              We reserve the right to remove content and terminate accounts that violate this policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>8. Content Moderation and Removal</h2>
            <p className={styles.paragraph}>
              We reserve the right to review, moderate, and remove any User Content that violates these Terms or applicable laws. 
              We may use automated tools and human reviewers to enforce our content policies. 
              However, we are not obligated to monitor all content and do not guarantee the accuracy or legality of User Content.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>9. Third-Party Services and Content</h2>
            <p className={styles.paragraph}>
              Our Service may integrate with third-party services (e.g., social media platforms, payment processors, AI providers). 
              Your use of these services is governed by their respective terms and privacy policies. 
              We are not responsible for third-party services or their content.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>10. Disclaimers and Limitations of Liability</h2>
            
            <h3 className={styles.subsectionTitle}>10.1 Service "As Is"</h3>
            <p className={styles.paragraph}>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. 
              WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            </p>

            <h3 className={styles.subsectionTitle}>10.2 AI-Generated Content</h3>
            <p className={styles.paragraph}>
              We do not guarantee the accuracy, quality, or suitability of AI-generated content. 
              You are solely responsible for reviewing and verifying all content before use or publication.
            </p>

            <h3 className={styles.subsectionTitle}>10.3 Limitation of Liability</h3>
            <p className={styles.paragraph}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, KINGDOM STUDIO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY 
              OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className={styles.paragraph}>
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT 
              GIVING RISE TO LIABILITY.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>11. Indemnification</h2>
            <p className={styles.paragraph}>
              You agree to indemnify, defend, and hold harmless Kingdom Studio, its affiliates, officers, directors, employees, 
              and agents from any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) 
              arising from:
            </p>
            <ul className={styles.list}>
              <li>Your use of the Service</li>
              <li>Your User Content</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>12. Copyright and DMCA</h2>
            <p className={styles.paragraph}>
              We respect intellectual property rights and expect users to do the same. If you believe your copyrighted work 
              has been infringed, please contact us at info@kingdomstudio.pro with:
            </p>
            <ul className={styles.list}>
              <li>Description of the copyrighted work</li>
              <li>Location of the infringing material on our Service</li>
              <li>Your contact information</li>
              <li>A statement of good faith belief that use is not authorized</li>
              <li>A statement that the information is accurate and you are authorized to act</li>
              <li>Your physical or electronic signature</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>13. Termination</h2>
            <p className={styles.paragraph}>
              We may suspend or terminate your account at any time for violations of these Terms or for any other reason 
              at our sole discretion. Upon termination:
            </p>
            <ul className={styles.list}>
              <li>Your right to use the Service will immediately cease</li>
              <li>We may delete your User Content after a reasonable grace period</li>
              <li>You remain liable for all charges incurred before termination</li>
              <li>Provisions that should survive termination will continue to apply</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>14. Dispute Resolution</h2>
            
            <h3 className={styles.subsectionTitle}>14.1 Informal Resolution</h3>
            <p className={styles.paragraph}>
              Before filing a claim, you agree to contact us at info@kingdomstudio.pro to attempt to resolve the dispute informally.
            </p>

            <h3 className={styles.subsectionTitle}>14.2 Governing Law</h3>
            <p className={styles.paragraph}>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which 
              Kingdom Studio operates, without regard to conflict of law principles.
            </p>

            <h3 className={styles.subsectionTitle}>14.3 Arbitration</h3>
            <p className={styles.paragraph}>
              Any disputes arising from these Terms or the Service shall be resolved through binding arbitration, 
              except where prohibited by law. You waive your right to a jury trial or to participate in a class action.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>15. Changes to Terms</h2>
            <p className={styles.paragraph}>
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email 
              or through the Service. Your continued use of the Service after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>16. Miscellaneous</h2>
            
            <h3 className={styles.subsectionTitle}>16.1 Entire Agreement</h3>
            <p className={styles.paragraph}>
              These Terms constitute the entire agreement between you and Kingdom Studio regarding the Service.
            </p>

            <h3 className={styles.subsectionTitle}>16.2 Severability</h3>
            <p className={styles.paragraph}>
              If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full effect.
            </p>

            <h3 className={styles.subsectionTitle}>16.3 Waiver</h3>
            <p className={styles.paragraph}>
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
            </p>

            <h3 className={styles.subsectionTitle}>16.4 Assignment</h3>
            <p className={styles.paragraph}>
              You may not assign or transfer these Terms without our prior written consent. 
              We may assign these Terms without restriction.
            </p>

            <h3 className={styles.subsectionTitle}>16.5 No Agency</h3>
            <p className={styles.paragraph}>
              Nothing in these Terms creates any agency, partnership, or joint venture between you and Kingdom Studio.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>17. Contact Information</h2>
            <p className={styles.paragraph}>
              If you have any questions about these Terms, please contact us:
            </p>
            <ul className={styles.list}>
              <li>Email: info@kingdomstudio.pro</li>
              <li>Website: kingdomstudio.pro</li>
            </ul>
          </section>

          <section className={styles.section}>
            <div className={styles.acknowledgment}>
              <p className={styles.paragraph}>
                <strong>By using Kingdom Studio, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong>
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
